<script setup lang="ts">
import DocumentAccordion from "@/components/DocumentAccordion.vue"
import Pdf from "@/components/Pdf.vue"
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import type { ChampionshipRegistrationFinalDocumentsTask } from "@/model/championship/championship-registration-final-documents-task"
import type { Invoice } from "@/model/order/invoice"
import { InvoiceType } from "@/model/order/invoice-type"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { useApp, useHttpGet } from "@/vf"
import { computed, ref, toRefs } from "vue"
import type { ChampionshipRegistrationFinalDocumentsData } from "./championship-registration"
import ChampionshipRegistrationResetButton from "./internal/ChampionshipRegistrationResetButton.vue"
import { useRegistrationPolicy } from "./useRegistrationPolicy"

const props = defineProps<TasksProps<ChampionshipRegistrationFinalDocumentsTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const { data } = await useHttpGet<ChampionshipRegistrationFinalDocumentsData>(
    `/championship/registration/${task.value.organizationId}/final-documents/${task.value.id}`,
)
const { apiLink } = useApp()
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => props.viewMode),
)

const openDocument = ref<string>(data.value?.invoices?.length ? "" : "orderConfirmation")
const invoices = computed(() => data.value?.invoices ?? [])

function toggleDocument(document: string) {
    openDocument.value = openDocument.value === document ? "" : document
}

function invoiceLink(invoice: Invoice) {
    return `/championship/registration/${data.value?.registrationId}/invoice/${invoice.id}`
}

function invoiceTitle(invoice: Invoice) {
    const label = invoice.type === InvoiceType.CreditNote ? trans("credit_note") : trans("invoice")
    return invoice.invoiceNumber ? `${label} ${invoice.invoiceNumber}` : label
}

const trans = defineTranslations({
    order_confirmation: {
        de: "Auftragsbestätigung",
        en: "Order confirmation",
    },
    order_confirmation_download: {
        de: "Auftragsbestätigung herunterladen",
        en: "Download order confirmation",
    },
    invoice: {
        de: "Rechnung",
        en: "Invoice",
    },
    invoice_download: {
        de: "Rechnung herunterladen",
        en: "Download invoice",
    },
    credit_note: {
        de: "Gutschrift",
        en: "Credit note",
    },
    credit_note_download: {
        de: "Gutschrift herunterladen",
        en: "Download credit note",
    },
    done_befor_taxpoint: {
        de:
            "Super – Du hast alle Tasks abgeschlossen und bist somit startklar!<br>" +
            "<br>" +
            "Vor der Meisterschaft findest Du hier Eure Auftragsbestätigung.<br>" +
            "Nach dem Event wird die Rechnung von unserem Office an dieser Stelle bereitgestellt.<br>" +
            "<br>" +
            "Bleib dran – wir freuen uns schon auf die {event_name} mit Euch!",
        en:
            "Awesome – you’ve completed all your tasks and are ready to go! <br>" +
            "<br>" +
            "Before the championship, you’ll find your order confirmation here.<br>" +
            "After the event, the invoice will be made available in this section.<br>" +
            "<br>" +
            "Stay tuned – we can’t wait to see you at {event_name}!<br>",
    },
})
</script>
<template>
    <div class="p-5">
        <div class="row">
            <div class="col-xl-7 col-lg-6">
                <DocumentAccordion
                    :title="trans('order_confirmation')"
                    :open="openDocument === 'orderConfirmation'"
                    @toggle="toggleDocument('orderConfirmation')"
                >
                    <div class="text-center mb-3">
                        <a
                            :href="
                                apiLink(
                                    `/championship/registration/${data?.registrationId}/latest-order-confirmation?download=1`,
                                )
                            "
                            class="btn btn-primary"
                        >
                            <i class="fa fa-fw fa-download"></i>
                            {{ trans("order_confirmation_download") }}
                        </a>
                    </div>
                    <Pdf :file="`/championship/registration/${data?.registrationId}/latest-order-confirmation`"></Pdf>
                </DocumentAccordion>

                <DocumentAccordion
                    v-for="invoice in invoices"
                    :key="invoice.id"
                    class="mt-3"
                    :title="invoiceTitle(invoice)"
                    :open="openDocument === invoice.id"
                    @toggle="toggleDocument(invoice.id!)"
                >
                    <div class="text-center mb-3">
                        <a :href="apiLink(invoiceLink(invoice) + '?download=1')" class="btn btn-primary">
                            <i class="fa fa-fw fa-download"></i>
                            {{
                                invoice.type === InvoiceType.CreditNote
                                    ? trans("credit_note_download")
                                    : trans("invoice_download")
                            }}
                        </a>
                    </div>
                    <Pdf :file="invoiceLink(invoice)" />
                </DocumentAccordion>
            </div>

            <div class="col-xl-5 col-lg-6">
                <VarsityContentProcessor
                    :content="trans('done_befor_taxpoint', { event_name: data?.championshipName })"
                ></VarsityContentProcessor>
            </div>
        </div>

        <div class="row">
            <div class="col-xl-7 col-lg-6">
                <ChampionshipRegistrationResetButton
                    :registrationId="data!.registrationId"
                    :view-mode="props.viewMode"
                    @resetted="emit('updated')"
                    #default="{ renderButton, renderConfirm }"
                >
                    <div class="mt-5">
                        <component :is="renderButton" v-if="policy.ResetRegistration" />
                    </div>
                    <div>
                        <component :is="renderConfirm" />
                    </div>
                </ChampionshipRegistrationResetButton>
            </div>
        </div>
    </div>
</template>
